import React, { useState, useEffect, useCallback, useRef } from "react";
import CsvUploader from "./CsvUploader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { Upload, X } from "lucide-react";
import { PageHeader } from "../ui/caliper";

const GrnTemplate = () => {

    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    const [showOnlyNonAvailable, setShowOnlyNonAvailable] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [showAlternateModal, setShowAlternateModal] = useState(false);
    const [alternateSearchQuery, setAlternateSearchQuery] = useState("");
    const [alternateResults, setAlternateResults] = useState([]);
    const [alternateLoading, setAlternateLoading] = useState(false);
    const [alternateError, setAlternateError] = useState("");
    const [activeItem, setActiveItem] = useState(null);
    const searchControllerRef = useRef(null);

    const [data, setData] = useState([]);

    // Filter logic
    const filteredData = Array.isArray(data)
        ? (showOnlyNonAvailable
            ? data.filter(item => item.skuCode === "")
            : data)
        : [];

    const ELASTIC_SEARCH_ENDPOINT = process.env.REACT_APP_API_ES_URL;

    const handleOpenAlternateModal = (item) => {
        setActiveItem(item);
        setAlternateSearchQuery(item?.itemDetails || "");
        setAlternateResults([]);
        setAlternateError("");
        setShowAlternateModal(true);
    };

    const handleCloseAlternateModal = () => {
        setShowAlternateModal(false);
        setActiveItem(null);
        setAlternateLoading(false);
    };

    const handleSelectMedicine = (medicine) => {
        if (!activeItem) {
            return;
        }
        /* Coerce the first present, numeric-looking value; blank if none. */
        const numOr = (...vals) => {
            for (const v of vals) {
                if (v != null && v !== "") {
                    const n = Number(v);
                    if (!Number.isNaN(n)) return n;
                }
            }
            return "";
        };
        /* B2B ES record carries the pricing the GRN row needs: MRP (medicineRate /
           salePrice), the purchase price (purchasePrice) and trade price (ptr).
           Fill the required row fields on select. */
        const mrp = numOr(medicine.medicineRate, medicine.salePrice, medicine.unitPrice);
        const unitPrice = numOr(medicine.purchasePrice);
        const tradePrice = numOr(medicine.purchasePrice, medicine.ptr);
        setData(prev =>
            prev.map(item =>
                item.id === activeItem.id
                    ? {
                        ...item,
                        itemDetails: medicine.medicineName || item.itemDetails,
                        skuCode: medicine.id || item.skuCode,
                        mrp: mrp !== "" ? mrp : item.mrp,
                        unitPrice: unitPrice !== "" ? unitPrice : item.unitPrice,
                        tradePrice: tradePrice !== "" ? tradePrice : item.tradePrice,
                        drugAvailability: "Y",
                    }
                    : item
            )
        );
        setShowAlternateModal(false);
    };

    const handleAlternateSearch = useCallback(async () => {
        const query = alternateSearchQuery.trim();
        if (!query) {
            setAlternateError("");
            setAlternateResults([]);
            return;
        }

        if (!ELASTIC_SEARCH_ENDPOINT) {
            setAlternateError("Search service not configured.");
            return;
        }

        if (searchControllerRef.current) {
            searchControllerRef.current.abort();
        }

        const controller = new AbortController();
        searchControllerRef.current = controller;

        setAlternateLoading(true);
        setAlternateError("");

        try {
            const response = await fetch(
                `${ELASTIC_SEARCH_ENDPOINT}?freeTextSearch=${encodeURIComponent(query)}&pageNo=1&pageSize=20&source=B2B`,
                { signal: controller.signal }
            );

            if (!response.ok) {
                setAlternateResults([]);
                setAlternateError("No medicines available.");
                return;
            }

            const payload = await response.json();
            console.log("response es", payload);
            setAlternateResults(payload || []);
        } catch (error) {
            if (error.name === "AbortError") {
                return;
            }
            setAlternateResults([]);
            setAlternateError(error.message || "No medicines available.");
        } finally {
            if (searchControllerRef.current === controller) {
                searchControllerRef.current = null;
            }
            setAlternateLoading(false);
        }
    }, [alternateSearchQuery, ELASTIC_SEARCH_ENDPOINT]);

    useEffect(() => {
        if (!showAlternateModal) {
            return;
        }
        const debounceId = setTimeout(() => {
            handleAlternateSearch();
        }, 300);
        return () => clearTimeout(debounceId);
    }, [alternateSearchQuery, showAlternateModal, handleAlternateSearch]);

    const handleUpload = async () => {
        if (!selectedFile) {
            console.log("No file selected");
            return;
        }

        setUploadLoading(true);
        setUploadError("");

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            const response = await axios.post(
                `${apiUrl}grn/converter/excel-to-purchase-items`,
                formData
            );

            // Ensure we always set an array
            const responseData = response?.data?.purchaseItem || [];
            console.log("responseData", responseData);
            const itemsArray = Array.isArray(responseData) ? responseData : [];

            // Iterate and add index/id to each item
            const itemsWithIndex = itemsArray.map((item, index) => ({
                ...item,
                id: item.id || index + 1,
            }));

            setData(itemsWithIndex);
            setShowUploadModal(false);
            setSelectedFile(null);
        } catch (error) {
            setUploadError(error.message || "Failed to upload file. Please try again.");
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDelete = (id, name) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete "${name}"?`);
        if (confirmDelete) {
            setData(prev => prev.filter(item => item.id !== id));
        }
    };

    const validateDataBeforeExport = (data) => {
        for (let i = 0; i < data.length; i++) {
            const item = data[i];

            // Check if drugCode is empty
            if (!item.skuCode || item.skuCode.trim() === "") {
                alert(`Validation failed: Drug Code is empty for item "${item.itemDetails}"`);
                return false;
            }

            // Check billOfEntry format if not empty
            // if (item.billOfEntry && item.billOfEntry.trim() !== "") {
            //     const regex = /^\d+\+\d+$/; // format a+b where a and b are integers
            //     if (!regex.test(item.billOfEntry.trim())) {
            //         alert(`Validation failed: Bill Of Entry format invalid for item "${item.itemDetails}" (must be a+b)`);
            //         return false;
            //     }
            // }
        }

        return true;
    };

    const handleExportExcel = () => {

        if (!validateDataBeforeExport(filteredData)) {
            return;
        }

        const exportData = filteredData.map((item) => ({
            "Vendor Code*": item.vendorCode || "",
            "Vendor Invoice Number*": item.vendorInvoiceNumber || "",
            "Purchase Order Code": item.purchaseOrderCode || "",
            "Vendor Invoice Date*": item.vendorInvoiceDate || "",
            "Sku Code*": item.skuCode || "",
            "Qty*": item.qty || "",
            "Discount (%)": item.discountPercent || "",
            "Discount (Amount Value)": item.discountAmount || "",
            "Tax Type Code": item.taxTypeCode || "",
            "Item Code": item.itemCode || "",
            "Item Details": item.itemDetails || "",
            "GRN State": item.grnState || "",
            "Shelf Code": item.shelfCode || "",
            "MRP": item.mrp || "",
            "Unit Price": item.unitPrice || "",
            "Additional Cost": item.additionalCost || "",
            "Manufacturing Date": item.manufacturingDate || "",
            "Expiry date as dd/MM/yyyy": item.expiryDate || "",
            "Vendor Batch Number": item.vendorBatchNumber || "",
            "Country of Origin": item.countryOfOrigin || "",
            "Bill of Entry": item.billOfEntry || "",
            "Trade Price": item.tradePrice || "",
            "EAN": item.ean || "",
        }));


        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Drugs");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "Drugs_List.xlsx");
    };

    const handleDownloadTemplate = () => {
        // Define headers — full set of fields required to post a GRN line
        // (mirrors the GRN API line contract: code, HSN, batch, expiry, prices,
        // qty, free, discount, GST% and packing).
        const headers = [
            "Product Name",
            "Product Code",
            "HSN",
            "Batch",
            "Expiry",
            "MRP",
            "Purchase Price",
            "Sale Price",
            "QTY",
            "Free",
            "Discount Price",
            "GST %",
            "Packing"
        ];


        const ws = XLSX.utils.aoa_to_sheet([headers]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        saveAs(blob, "template.xlsx");
    };

    return (
        <>
          <div className="flex flex-col gap-4 cb-fade-in">
            <PageHeader
                title="GRN template"
                sub="Upload a purchase document and map drugs before posting"
            />

            {/* Top Rectangle */}
            <div className="bg-panel border border-hairline rounded-cb-md p-4 flex flex-wrap justify-between items-center gap-4 w-full">

                <p className="text-fg-muted text-t-sm max-w-md">
                    Please upload the related document. If you want, download the template{" "}
                    <button
                        onClick={handleDownloadTemplate}
                        className="text-fg-link font-medium underline"
                    >
                        here
                    </button>.
                </p>

                <button
                    onClick={() => setShowUploadModal(true)}
                    className="inline-flex items-center gap-1.5 h-8 px-4 text-t-md font-medium bg-ink-600 text-fg-on-ink rounded-cb-sm hover:bg-ink-700 transition-colors"
                >
                    <Upload size={14} strokeWidth={1.9} />
                    Upload
                </button>
            </div>

            {/* Table */}
            <div className="bg-panel border border-hairline rounded-cb-md overflow-hidden">

                {/* Toggle above table header */}
                <div className="flex flex-wrap justify-end items-center gap-4 px-3.5 py-2.5 border-b border-hairline-subtle">
                    {/* Toggle */}
                    <div className="flex items-center gap-2.5">
                        <span className="text-t-sm text-fg-default">Show only non-available items</span>

                        <button
                            onClick={() => setShowOnlyNonAvailable(!showOnlyNonAvailable)}
                            className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-colors
                ${showOnlyNonAvailable ? "bg-green-500" : "bg-neutral-200"}`}
                        >
                            <div
                                className={`bg-white w-4 h-4 rounded-full shadow-cb-sm transform transition-transform
                    ${showOnlyNonAvailable ? "translate-x-4" : "translate-x-0"}`}
                            ></div>
                        </button>
                    </div>

                    {/* Export Excel Button */}
                    <button
                        onClick={handleExportExcel}
                        className="inline-flex items-center h-8 px-4 bg-ink-600 text-fg-on-ink rounded-cb-sm text-t-md font-medium hover:bg-ink-700 transition-colors"
                    >
                        Export Excel
                    </button>
                </div>

                {/* SCROLL WRAPPER ADDED BELOW */}
                <div className="w-full overflow-x-auto cb-scroll">
                    <table className="w-full border-collapse text-t-sm min-w-max">
                        <thead>
                            <tr className="bg-surface-subtle text-left border-b border-hairline">
                                <th className="cb-th p-2 px-4">#</th>
                                <th className="cb-th p-2 px-4">Vendor Code*</th>
                                <th className="cb-th p-2 px-4">Vendor Invoice Number*</th>
                                <th className="cb-th p-2 px-4">Vendor Invoice Date*</th>
                                <th className="cb-th p-2 px-4">Sku Code*</th>
                                <th className="cb-th p-2 px-4">Qty*</th>
                                <th className="cb-th p-2 px-4">Item Code</th>
                                <th className="cb-th p-2 px-4">Item Details</th>
                                <th className="cb-th p-2 px-4">GRN State</th>
                                <th className="cb-th p-2 px-4">MRP</th>
                                <th className="cb-th p-2 px-4">Expiry date as dd/MM/yyyy</th>
                                <th className="cb-th p-2 px-4">Vendor Batch Number</th>
                                <th className="cb-th p-2 px-4">Bill of Entry</th>
                                <th className="cb-th p-2 px-4">Trade Price</th>
                                <th className="cb-th p-2 px-4">Discount (%)</th>
                                <th className="cb-th p-2 px-4">Discount (Amount Value)</th>
                                <th className="cb-th p-2 px-4">Tax Type Code</th>
                                <th className="cb-th p-2 px-4">Purchase Order Code</th>
                                <th className="cb-th p-2 px-4">Shelf Code</th>
                                <th className="cb-th p-2 px-4">Unit Price</th>
                                <th className="cb-th p-2 px-4">Additional Cost</th>
                                <th className="cb-th p-2 px-4">Manufacturing Date</th>
                                <th className="cb-th p-2 px-4">Country of Origin</th>
                                <th className="cb-th p-2 px-4">EAN</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredData.length === 0 && (
                                <tr>
                                    <td colSpan={24} className="px-4 py-12 text-center text-fg-muted text-t-sm">
                                        No items yet — upload a GRN document to begin.
                                    </td>
                                </tr>
                            )}
                            {filteredData.map((item) => (
                                <tr key={item.id} className="border-b border-hairline-subtle text-t-xs hover:bg-surface-hover transition-colors">
                                    {/* # (Edit/Delete) */}
                                    <td className="p-2 flex gap-1.5">
                                        <button
                                            className="p-1 text-green-700 hover:bg-green-50 rounded-cb-sm transition-colors"
                                            onClick={() => handleOpenAlternateModal(item)}
                                        >
                                            <FiEdit size={16} />
                                        </button>

                                        <button
                                            className="p-1 text-red-700 hover:bg-red-50 rounded-cb-sm transition-colors"
                                            onClick={() => handleDelete(item.id, item.itemDetails)}
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </td>
                                    <td className="p-2 px-4">{item.vendorCode}</td>
                                    <td className="p-2 px-4">{item.vendorInvoiceNumber}</td>
                                    <td className="p-2 px-4">{item.vendorInvoiceDate}</td>
                                    <td className="p-2 px-4">{item.skuCode}</td>
                                    <td className="p-2 px-4">{item.qty}</td>
                                    <td className="p-2 px-4">{item.itemCode}</td>
                                    <td className="p-2 px-4">{item.itemDetails}</td>
                                    <td className="p-2 px-4">{item.grnState}</td>
                                    <td className="p-2 px-4">{item.mrp}</td>
                                    <td className="p-2 px-4">{item.expiryDate}</td>
                                    <td className="p-2 px-4">{item.vendorBatchNumber}</td>
                                    <td className="p-2 px-4">{item.billOfEntry}</td>
                                    <td className="p-2 px-4">{item.tradePrice}</td>
                                    <td className="p-2 px-4">{item.discountPercent}</td>
                                    <td className="p-2 px-4">{item.discountAmount}</td>
                                    <td className="p-2 px-4">{item.taxTypeCode}</td>
                                    <td className="p-2 px-4">{item.purchaseOrderCode}</td>
                                    <td className="p-2 px-4">{item.shelfCode}</td>
                                    <td className="p-2 px-4">{item.unitPrice}</td>
                                    <td className="p-2 px-4">{item.additionalCost}</td>
                                    <td className="p-2 px-4">{item.manufacturingDate}</td>
                                    <td className="p-2 px-4">{item.countryOfOrigin}</td>
                                    <td className="p-2 px-4">{item.ean}</td>
                                </tr>
                            ))}
                        </tbody>

                    </table>
                </div>
            </div>
          </div>

            {showUploadModal && (
                <div className="fixed inset-0 flex justify-center items-center z-50 cb-fade-in p-2 sm:p-0" style={{ background: "var(--bg-overlay)" }}>
                    <div className="bg-panel border border-hairline-subtle p-6 rounded-cb-lg shadow-cb-lg w-full max-w-sm relative cb-animate-in">

                        {/* Close Button */}
                        <button
                            onClick={() => setShowUploadModal(false)}
                            className="absolute top-3 right-3 text-fg-muted hover:text-fg-strong"
                        >
                            <X size={18} strokeWidth={1.9} />
                        </button>

                        <h2 className="text-t-lg font-semibold text-fg-strong mb-1">Upload file</h2>
                        <p className="text-t-sm text-fg-muted mb-4">
                            Choose a <b>CSV</b> or <b>Excel</b> file to upload.
                        </p>


                        <CsvUploader onFileSelect={(file) => setSelectedFile(file)} />

                        {uploadError && (
                            <p className="text-t-sm text-red-700 mt-3">{uploadError}</p>
                        )}

                        <button
                            onClick={handleUpload}
                            disabled={!selectedFile || uploadLoading}
                            className={`mt-5 w-full h-9 rounded-cb-sm text-t-md font-medium transition-colors
                    ${selectedFile && !uploadLoading ? "bg-ink-600 hover:bg-ink-700 text-fg-on-ink" :
                                    "bg-sunken text-fg-subtle cursor-not-allowed"}`}
                        >
                            {uploadLoading ? "Uploading…" : "Upload file"}
                        </button>


                    </div>
                </div>
            )}

            {showAlternateModal && (
                <div className="fixed inset-0 flex justify-center items-center z-50 cb-fade-in p-2 sm:p-0" style={{ background: "var(--bg-overlay)" }}>
                    <div className="bg-panel border border-hairline-subtle p-6 rounded-cb-lg shadow-cb-lg w-11/12 max-w-lg relative cb-animate-in">
                        <button
                            onClick={handleCloseAlternateModal}
                            className="absolute top-3 right-3 text-fg-muted hover:text-fg-strong"
                        >
                            <X size={18} strokeWidth={1.9} />
                        </button>

                        <h2 className="text-t-lg font-semibold text-fg-strong mb-1">Find medicine</h2>
                        {activeItem && (
                            <p className="text-t-sm text-fg-muted mb-4">
                                Searching drug details for <b>{activeItem.itemDetails}</b>
                            </p>
                        )}

                        <div className="flex flex-col gap-2">
                            <input
                                type="text"
                                value={alternateSearchQuery}
                                onChange={(e) => setAlternateSearchQuery(e.target.value)}
                                className="h-9 border border-hairline rounded-cb-sm px-3 text-t-md outline-none focus:border-ink-500 focus:shadow-cb-focus transition-shadow"
                                placeholder="Type a medicine name"
                            />

                            <div className="flex justify-end">
                                <button
                                    onClick={handleAlternateSearch}
                                    className="h-8 px-4 bg-ink-600 text-fg-on-ink text-t-md font-medium rounded-cb-sm hover:bg-ink-700 disabled:opacity-50 transition-colors"
                                    disabled={alternateLoading}
                                >
                                    {alternateLoading ? "Searching…" : "Search"}
                                </button>
                            </div>
                        </div>

                        {alternateError && (
                            <p className="text-t-sm text-red-700 mt-3">{alternateError}</p>
                        )}

                        <div className="mt-4 max-h-60 min-h-[160px] overflow-y-auto cb-scroll border border-hairline rounded-cb-sm p-2 bg-surface-subtle">
                            {alternateResults.length === 0 && !alternateLoading && !alternateError && (
                                <p className="text-t-sm text-fg-muted p-2">No results yet. Try searching.</p>
                            )}

                            {alternateResults.length > 0 && (
                                <ul className="flex flex-col gap-1 text-t-sm">
                                    {alternateResults.map((medicine) => (
                                        <li
                                            key={`${medicine.id}`}
                                            className="p-2 bg-panel border border-hairline rounded-cb-sm hover:bg-surface-hover hover:border-ink-300 cursor-pointer transition-colors"
                                            onClick={() => handleSelectMedicine(medicine)}
                                        >
                                            <p className="font-medium text-fg-strong">{medicine.medicineName || "-"}</p>
                                            <div className="flex justify-between text-t-xs text-fg-muted font-mono mt-0.5">
                                                <span>{medicine.id}</span>
                                                {medicine.manufacturer && (
                                                    <span className="ml-2 text-right truncate">
                                                        {medicine.manufacturer}
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default GrnTemplate